import React, { type JSX } from "react";

const PANEL_CLASS = "bg-white rounded-lg shadow-xl max-w-lg w-full";
const BUTTON_BASE =
  "px-4 py-2 text-sm font-medium rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

/**
 * Backdrop + centred dialog + white panel shared by the chat modals (rename,
 * delete, search). Mouse-down outside the panel calls `onClose`.
 */
export default function ChatModalFrame({
  titleId,
  onClose,
  panelClassName = PANEL_CLASS,
  children,
}: {
  titleId: string;
  onClose: () => void;
  panelClassName?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onMouseDown={onClose} aria-hidden="true" />

      {/* Dialog */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={onClose}
      >
        <div className={panelClassName} onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
          {children}
        </div>
      </div>
    </>
  );
}

/** Bordered title bar for a chat modal. */
export function ChatModalHeader({ titleId, title }: { titleId: string; title: string }): JSX.Element {
  return (
    <div className="px-6 py-4 border-b border-gray-200">
      <h2 id={titleId} className="text-lg font-semibold text-gray-900">
        {title}
      </h2>
    </div>
  );
}

/** Cancel + primary action footer; `isBusy` disables both and swaps in `labels.busy`. */
export function ChatModalFooter({
  onCancel,
  onConfirm,
  isBusy,
  isConfirmDisabled,
  labels,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  isBusy: boolean;
  isConfirmDisabled: boolean;
  labels: { confirm: string; busy: string };
}): JSX.Element {
  return (
    <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
      <button
        onClick={onCancel}
        disabled={isBusy}
        className={`${BUTTON_BASE} text-gray-700 border border-gray-300 hover:bg-gray-50`}
      >
        Cancel
      </button>
      <button
        onClick={onConfirm}
        disabled={isConfirmDisabled}
        className={`${BUTTON_BASE} text-white`}
        style={{ backgroundColor: "#234E81" }}
      >
        {isBusy ? labels.busy : labels.confirm}
      </button>
    </div>
  );
}
